import React, { createContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';

const COLOR_MODE_STORAGE_KEY = 'colorMode';

const getInitialMode = () => {
    if (typeof window === 'undefined') return 'light';
    const stored = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Both `mode` (v5-alpha core) and `type` (v4 lab/pickers) are set so
// cross-version MUI packages in this app theme consistently.
const buildPalette = (mode) => {
    if (mode === 'dark') {
        return {
            mode,
            type: mode,
            background: { default: '#121212', paper: '#1e1e1e' },
            text: {
                primary: '#ffffff',
                secondary: 'rgba(255, 255, 255, 0.7)',
                disabled: 'rgba(255, 255, 255, 0.5)'
            }
        };
    }
    return { mode, type: mode };
};

const buildTheme = (mode) => createMuiTheme({
    palette: buildPalette(mode),
    typography: {
        fontFamily: [
            'Nunito',
            'Roboto',
            'sans-serif'
        ].join(','),
    }
});

const ColorModeContext = createContext({ mode: 'light', toggleMode: () => {} });

const ColorModeProvider = props => {
    const [mode, setMode] = useState(getInitialMode);

    const toggleMode = () => {
        setMode(prev => {
            const next = prev === 'dark' ? 'light' : 'dark';
            window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, next);
            return next;
        });
    };

    const theme = useMemo(() => buildTheme(mode), [mode]);

    // Mirror mode onto <html> so non-MUI components (react-bootstrap Jumbotron,
    // Card, Modal, Form, etc.) can be themed via CSS attribute selectors.
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', mode);
    }, [mode]);

    return (
        <ColorModeContext.Provider value={{ mode, toggleMode }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {props.children}
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export { ColorModeProvider, ColorModeContext };
